import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, EventEmitter, input, Input, OnInit, Output, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { ChatsModel, ContextApiInputModel, ContextGlInputModel, ContextGlTbModel, ContextOAModel, LucaEngListModel, SavedChatHistoryModel } from 'countable@model';
import { AiService } from 'countable@services';
import moment from 'moment';
import { AiFreeContextUtility } from 'src/app/helpers/ai-free-context-util';
import { ContextGLYearModel, ContextInputModel, ContextOtherApiModel, ContextResModel, freeContextOutputModel, GroupModel, SaveOtherApiModel, tokenUsageModel, YEARS_LIST } from 'src/app/model/ai/context.model';


@Component({
  selector: 'app-ai-context',
  templateUrl: './ai-context.component.html',
  styleUrl: './ai-context.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AiContextComponent implements OnInit {
  @ViewChild(MatSort) sort: MatSort;
  @Output() closeContext: EventEmitter<Partial<freeContextOutputModel>> = new EventEmitter<Partial<freeContextOutputModel>>(null);

  private selectedChat: WritableSignal<ChatsModel> = signal<ChatsModel>(null);
  private chatHistory: WritableSignal<SavedChatHistoryModel> = signal<SavedChatHistoryModel>(null);
  public contextInfo: WritableSignal<ContextResModel> = signal<ContextResModel>(null);
  public selectedEng: WritableSignal<LucaEngListModel> = signal<LucaEngListModel>(null);
  public originalContext: WritableSignal<ContextApiInputModel> = signal<ContextApiInputModel>(null);
  public isLoading: WritableSignal<boolean> = signal<boolean>(true);
  public isContextLoading: Signal<boolean> =computed(() => {
    return (this.hasTbRows() ? this.hasTbRows() : this.isLoading())
  })
  public isWindowMode: WritableSignal<boolean> = signal<boolean>(false);

  @Input() set contextInputs(inputs: ContextInputModel) {
      this.selectedEng.set(inputs?.selectedEng);
      if (this.selectedEng()?.engId > 0) {
        this.setContextWindow(inputs);
      }
      this.isLoading.set(inputs?.isLoading);
  }

  @Input() set isFullWindow(value: boolean) {
    this.isWindowMode.set(value);
  }

  public aiFreeContextUtil: typeof AiFreeContextUtility = AiFreeContextUtility;
  public searchValue: WritableSignal<string> = signal<string>(null);
  public isSelectedAll: WritableSignal<boolean> = signal<boolean>(false);
  public isTbSelectedAll: WritableSignal<boolean> = signal<boolean>(false);
  public isOASelectedAll: WritableSignal<boolean> = signal<boolean>(false);
  public tbYearsList: WritableSignal<ContextGLYearModel[]> = signal<ContextGLYearModel[]>(JSON.parse(JSON.stringify(YEARS_LIST)));
  public gLYearsList: WritableSignal<ContextGLYearModel[]> = signal<ContextGLYearModel[]>(JSON.parse(JSON.stringify(YEARS_LIST)));
  public oaYearsList: WritableSignal<ContextGLYearModel[]> = signal<ContextGLYearModel[]>(JSON.parse(JSON.stringify(YEARS_LIST)));
  public displayedColumns: WritableSignal<string[]> = signal<string[]>(['accountCode', 'name', 'groupName', 'units']);
  public displayedOAColumns: WritableSignal<string[]> = signal<string[]>(['name', 'description', 'units']);
  public contextDataSource: MatTableDataSource<ContextGlTbModel> = null;
  public contextTbDataSource: MatTableDataSource<ContextGlTbModel> = new MatTableDataSource<ContextGlTbModel>();
  public contextOADataSource: MatTableDataSource<ContextOtherApiModel> = new MatTableDataSource<ContextOtherApiModel>();
  public tokensUsage: WritableSignal<tokenUsageModel> = signal<tokenUsageModel>(new tokenUsageModel());
  public selectedIndex: WritableSignal<number> = signal<number>(0);
  public searchTbValue: WritableSignal<string> = signal<string>(null);
  public hasTbRows: Signal<boolean> = computed(() => {
    return (this.contextInfo()?.trialBalance?.length > 0);
  });
  public hasActiveSource: Signal<boolean> = computed(() => {
    return (this.selectedEng()?.engId > 0 && this.selectedEng()?.integrationId > 0 && this.selectedEng()?.isActiveConnection);
  });
  public groupsTb: WritableSignal<GroupModel[]> = signal<GroupModel[]>([]);
  public groupsGl: WritableSignal<GroupModel[]> = signal<GroupModel[]>([]);
  public otherApi: WritableSignal<ContextOtherApiModel[]> = signal<ContextOtherApiModel[]>([]);
  public searchOA: WritableSignal<string> = signal<string>(null);
  public isOtherApiLoading: Signal<boolean> = computed(() => {
    return (this.otherApi()?.length > 0 && this.otherApi()?.some(o => o?.isLoading === true));
  })

  constructor(private aiService: AiService,private router: Router,
              private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
  }

  private setContextWindow(inputs: ContextInputModel) {
    this.selectedChat.set(inputs?.selectedChat);
    this.chatHistory.set(inputs?.chatHistory);
    this.contextInfo.set(inputs?.contextInfo);
    this.otherApi.set(inputs?.contextInfo?.apiContext?.selectedApiIds);
    this.originalContext.set(inputs?.chatHistory?.freeContext);
    if (this.contextInfo()?.trialBalance?.length > 0) {
      this.getContextDetails();
    } else {
      this.resetYearsList('GL');
      this.resetYearsList('TB');
    }
    this.isLoading.set(false);
    this.setOtherApiContext();
  }

  trackByIndex(index, item) {
    return item.id; // or any other unique identifier
  }

  trackByTbIndex(index, item) {
    return item.id; // or any other unique identifier
  }

  trackByOAIndex(index, item) {
    return item.id; // or any other unique identifier
  }

  public sortData(sort: Sort) {
    if (!sort.active || sort.direction === '') {
      return;
    }
    if (sort.active === 'units') {
      const isAsc = sort.direction === 'asc';
      let sortingData = isAsc ? this.contextDataSource.data.sort((a, b) => a.totalUnits - b.totalUnits) : this.contextDataSource.data.sort((a, b) => b.totalUnits - a.totalUnits);
      this.bindGrid(sortingData);
    } else {
      let sortingData = this.contextDataSource.data;
      const data = (sortingData).slice();
      sortingData = data.sort((a, b) => {
        const isAsc = sort.direction === 'asc';
        switch (sort.active) {
          case 'accountCode':
            return isAsc
              ? a.accountCode.localeCompare(b.accountCode, undefined, {numeric: true})
              : b.accountCode.localeCompare(a.accountCode, undefined, {numeric: true});
          case 'name':
            return this.compare(a.name, b.name, isAsc);
          default:
            return 0;
        }
      });
      this.bindGrid(sortingData);
    }

  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? 1 : -1) * (isAsc ? 1 : -1);
  }

  private bindGrid(list: ContextGlTbModel[] = []) {
    this.contextDataSource = new MatTableDataSource(list);
    this.contextDataSource.filterPredicate = ((data: ContextGlTbModel, filter: string) => {
      const inputSearch = (this.searchValue() ? (data?.accountCode?.toLowerCase()?.includes(this.searchValue()) ||
        data?.name?.toLowerCase()?.includes(this.searchValue()) || data?.groupName?.toLowerCase()?.includes(this.searchValue()) ||
        (data?.units + 'k').toLowerCase()?.includes(this.searchValue())) : true);
      const finGroup = this.groupsGl().find(group => group?.isChecked) ? this.groupsGl().filter(group => (group?.isChecked && group?.id === data?.finGroupId))?.length > 0 :
        this.groupsGl().filter(group => (!group.isChecked))?.length === this.groupsGl()?.length;
      return inputSearch && finGroup;
    }) as (ContextGlTbModel, string) => boolean;
    this.refreshDataSource();
  }

  public onChangeYearList(value: boolean, index: number) {
    this.gLYearsList.update(years => {
      years?.forEach((y, i) => {
        if (i === index) {
          y.isChecked = value;
        }
      });
      return [...years];
    });
    this.updateGlTokens();
  }

  private updateGlTokens() {
    const filteredKeys = this.gLYearsList()?.filter(key => key.isChecked === true);
    const len = this.gLYearsList()?.filter(key => key.isChecked)?.length;
    if (this.searchValue()) {
      this.contextDataSource.filteredData.forEach(item => {
        item.units = 0;
        item.totalUnits = 0;
      });
      const dataFilters = this.aiFreeContextUtil.updateDataSource(this.contextDataSource.filteredData, filteredKeys, len);
      this.contextDataSource.filteredData = dataFilters.dataSource;
    } else {
      this.contextDataSource.data.forEach(item => {
        item.units = 0;
        item.totalUnits = 0;
      });
      const dataFilters = this.aiFreeContextUtil.updateDataSource(this.contextDataSource.data, filteredKeys, len);
      this.contextDataSource.data = dataFilters.dataSource;
    }
    // To Do Calculate All Tokens in one Method and Calculate Individual Tab Tokens
    this.calculateTotalTokens();

  }

  public selectAllRows(value: boolean, type: string) {
    if (type && type === 'all') {
      this.resetFilters();
    }
    if (this.searchValue()) {
      this.contextDataSource.filteredData.forEach(item => {
        item.isRowChecked = value;
      });
    } else {
      this.contextDataSource.data.forEach(item => {
        item.isRowChecked = value;
      });
    }

    this.refreshDataSource();
  }

  public selectAllTbRows(value: boolean, type: string) {
    if (type && type === 'all') {
      this.resetTbFilters();
    }
    if (this.searchTbValue()) {
      this.contextTbDataSource.filteredData.forEach(item => {
        item.isRowChecked = value;
      });
    } else {
      this.contextTbDataSource.data.forEach(item => {
        item.isRowChecked = value;
      });
    }
    this.cdr.markForCheck();
    this.refreshTbDataSource();
  }

  public onChangeRow(value: boolean, item: ContextGlTbModel) {
    if (this.searchValue()) {
      this.contextDataSource.filteredData[this.getIndexRow(item)].isRowChecked = value;
    } else {
      this.contextDataSource.data[this.getIndexRow(item)].isRowChecked = value;
    }
    this.refreshDataSource();
  }

  private refreshDataSource() {
    this.isSelectedAll.set(
      this.searchValue() ?
        ((this.contextDataSource.filteredData?.filter(g => g?.isRowChecked === true)?.length) === (this.contextDataSource.filteredData?.length)) :
        ((this.contextDataSource.data?.filter(g => g?.isRowChecked === true)?.length) === (this.contextDataSource.data.length))
    );
    this.updateGlTokens();
  }

  private getContextDetails() {
    const groups: GroupModel[] = this.contextInfo()?.groups?.map(g => { return {...g, isChecked: false}; });
    this.groupsTb.set(
      JSON.parse(JSON.stringify(groups))
    );
    this.groupsGl.set(
      JSON.parse(JSON.stringify(groups))
    );
    const contextInfo: ContextResModel = JSON.parse(JSON.stringify(this.contextInfo()));
    const tbData = this.aiFreeContextUtil?.transformContextInfo(JSON.parse(JSON.stringify(contextInfo?.trialBalance)), [...contextInfo?.groups]);
    this.setGlContext(JSON.parse(JSON.stringify(tbData)), {...contextInfo});
    this.setTbContext(JSON.parse(JSON.stringify(tbData)), {...contextInfo});
    this.tokensUsage.update(tokens => {
      tokens.maxTokens = Math.round((this.contextInfo().maxTokensAllowed / 1000));
      return {...tokens};
    });
  }

  setGlContext(rows: ContextGlTbModel[], contextInfo: ContextResModel) {
    if (this.chatHistory()?.freeContext?.generalLedger?.trialBalanceIds?.length) {
      this.chatHistory()?.freeContext?.generalLedger?.trialBalanceIds?.forEach(id => {
        rows?.forEach(row => {
          if (id === row?.uuid) {
            row.isRowChecked = true;
          }
        });
      });
      this.gLYearsList.update((years) => {
        years?.forEach((y, i) => {
          if (i === 0) {
            y.isChecked = this.chatHistory()?.freeContext?.generalLedger?.isCYSelected;
          }
          if (i === 1) {
            y.isChecked = this.chatHistory()?.freeContext?.generalLedger?.isPY1Selected;
          }
          if (i === 2) {
            y.isChecked = this.chatHistory()?.freeContext?.generalLedger?.isPY2Selected;
          }
          y.isDisabled = !contextInfo[y.value];
        });
        return [...years];
      });
      this.groupsGl.update((groups) => {
        groups?.forEach(g => {
          if (this.chatHistory()?.freeContext?.generalLedger?.selectedFinGroupIds?.includes(g?.id)) {
            g.isChecked = true;
          }
        });
        return [...groups];
      });
      // To Do For Changing Groups Information
    } else {
      const years: ContextGLYearModel[] = JSON.parse(JSON.stringify(this.gLYearsList()));
      years?.forEach((y, i) => {
        y.isChecked = (i === 0);
        y.isDisabled = !contextInfo[y.value];
      });
      this.gLYearsList.set([...years]);
    }
    this.bindGrid(JSON.parse(JSON.stringify(rows)));
    if (!rows?.length) {
      this.resetYearsList('GL');
    }
    this.contextDataSource.filter = 'groupName';
  }

  setTbContext(rows: ContextGlTbModel[], contextInfo: ContextResModel) {
    if (this.chatHistory()?.freeContext?.trialBalance?.trialBalanceIds?.length) {
      this.tbYearsList.update((years) => {
        years?.forEach((y, i) => {
          if (i === 0) {
            y.isChecked = this.chatHistory()?.freeContext?.trialBalance?.isCYSelected;
          }
          if (i === 1) {
            y.isChecked = this.chatHistory()?.freeContext?.trialBalance?.isPY1Selected;
          }
          if (i === 2) {
            y.isChecked = this.chatHistory()?.freeContext?.trialBalance?.isPY2Selected;
          }
        });
        return [...years];
      });
      this.chatHistory()?.freeContext?.trialBalance?.trialBalanceIds?.forEach(id => {
        rows?.forEach(row => {
          if (id === row?.uuid) {
            row.isRowChecked = true;
          }
        });
      });
      this.groupsTb()?.forEach(g => {
        if (this.chatHistory()?.freeContext?.trialBalance?.selectedFinGroupIds?.includes(g?.id)) {
          g.isChecked = true;
        }
      });
      this.tbYearsList.update((years) => {
        years?.forEach(y => {
          y.isDisabled = !contextInfo[y.value];
        });
        return [...years];
      });

      // To Do For Changing Groups Information
    } else {
      this.tbYearsList.update((years) => {
        years?.forEach((y, i) => {
          y.isChecked = i === 0;
          y.isDisabled = !contextInfo[y.value];
        });
        return [...years];
      });
    }
    this.bindTBGrid(JSON.parse(JSON.stringify(rows)));
    if (!rows.length) {
      this.resetYearsList('TB');
    }
    this.contextTbDataSource.filter = 'groupName';
  }

  public onChangeTbGroups(event: MatCheckboxChange, groupId: number) {
    this.groupsTb.update((groups) => {
      groups?.forEach(g => {
        if (g.id === groupId) {
          g.isChecked = event?.checked;
        }
      });
      return [...groups];
    });
    // To Do Logic For Updating List
    this.contextTbDataSource.filter = 'groupName';
    this.updateTBTokens();
  }

  public onChangeGlGroups(event: MatCheckboxChange, groupId: number) {
    this.groupsGl.update((groups) => {
      groups?.forEach(g => {
        if (g.id === groupId) {
          g.isChecked = event?.checked;
        }
      });
      return [...groups];
    });
    this.contextDataSource.filter = 'groupName';
    this.updateGlTokens();
  }


  public unSelectionGroups() {
    this.groupsGl.update((groups) => {
      groups?.forEach(g => {
        g.isChecked = false;
      });
      return [...groups];
    });
    this.contextDataSource.filter = 'groupName';
    this.updateGlTokens();
  }

  public saveContext() {
    const context: ContextApiInputModel = new ContextApiInputModel();
    context.generalLedger = new ContextGlInputModel();
    context.trialBalance = new ContextGlInputModel();
    context.apiContext = new ContextOAModel();
    context.sourceLink = this.selectedEng()?.integrationId;
    context.yearEnd = moment(this.selectedEng()?.yearEnd).format('YYYY-MM-DD');
    this.contextDataSource.data.forEach(row => {
      if (row.isRowChecked) {
        context.generalLedger.trialBalanceIds.push(row.uuid);
      }
    });
    this.groupsGl.update((groups) => {
      groups?.forEach(g => {
        if (g?.isChecked) {
          context.generalLedger.selectedFinGroupIds.push(g.id);
        }
      });
      return [...groups];
    });
    const years = structuredClone(this.gLYearsList());
    context.generalLedger.isCYSelected = years?.some(y => y?.value === 'isCY' && y?.isChecked === true);
    context.generalLedger.isPY1Selected = years?.some(y => y?.value === 'isPY1' && y?.isChecked === true);
    context.generalLedger.isPY2Selected = years?.some(y => y?.value === 'isPY2' && y?.isChecked === true);
    this.contextTbDataSource.data.forEach(row => {
      if (row.isRowChecked) {
        context.trialBalance.trialBalanceIds.push(row.uuid);
      }
    });
    this.groupsTb.update((groups) => {
      groups?.forEach(g => {
        if (g?.isChecked) {
          context.trialBalance.selectedFinGroupIds.push(g.id);
        }
      });
      return [...groups];
    });
    const tbYears = structuredClone(this.tbYearsList());
    context.trialBalance.isCYSelected = tbYears?.some(y => y?.value === 'isCY' && y?.isChecked === true);
    context.trialBalance.isPY1Selected = tbYears?.some(y => y?.value === 'isPY1' && y?.isChecked === true);
    context.trialBalance.isPY2Selected = tbYears?.some(y => y?.value === 'isPY2' && y?.isChecked === true);
    this.contextOADataSource.data.forEach(row => {
      if (row.isRowChecked) {
        const api: ContextOtherApiModel = new ContextOtherApiModel();
        api.apiId = row?.apiId;
        api.content = row?.content;
        api.tokens = row?.tokens;
        api.cy = row?.cy;
        api.py1 = row?.py1;
        api.py2 = row?.py2;
        context.apiContext.selectedApiIds.push(api);
      }
    });
    context.apiContext.isCYSelected = this.oaYearsList()?.some(y => y?.value === 'isCY' && y?.isChecked === true);
    context.apiContext.isPY1Selected = this.oaYearsList()?.some(y => y?.value === 'isPY1' && y?.isChecked === true);
    context.apiContext.isPY2Selected = this.oaYearsList()?.some(y => y?.value === 'isPY2' && y?.isChecked === true);
    if (this.selectedChat()?.filePath) {
      this.selectedChat().freeContext = context;
      this.selectedChat().isFreeContextUpdated = true;
      this.aiService.createOrUpdateChat(this.selectedChat()).subscribe({
        next: res => {
          if (res?.status === 200 && res?.data) {
            this.closeContext.emit({
              actionType: 'SAVED',
              updatedContext: context,
              contextRes: JSON.parse(JSON.stringify(this.contextInfo()))
            });
          }
        }
      });
    } else {
      this.closeContext.emit({
        actionType: 'DRAFT',
        updatedContext: context,
        contextRes: JSON.parse(JSON.stringify(this.contextInfo()))
      });
    }
  }

  public applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue.set((filterValue.trim().length ? filterValue.trim().toLowerCase() : null));
    this.contextDataSource.filter = filterValue.trim().toLowerCase();
  }

  public applyTbFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchTbValue.set((filterValue.trim().length ? filterValue.trim().toLowerCase() : null));
    this.contextTbDataSource.filter = filterValue.trim().toLowerCase();
  }

  private setPercentage() {
    const circle = document.getElementById('progressCircle');
    if (circle) {
      const percentColor = this.aiFreeContextUtil.setPercentage(this.contextInfo().maxTokensAllowed, this.tokensUsage().totalTokens);
      circle.style.background = `conic-gradient(${percentColor.color} ${percentColor.percent}%, #e0e0e0 0%)`;
      this.tokensUsage.update(tokens => {
        tokens.percentage = percentColor.percent;
        return {...tokens};
      });
    }
    this.cdr.markForCheck();
  }

  private getIndexRow(row: ContextGlTbModel): number {
    if (this.searchValue()) {
      return this.contextDataSource.filteredData.indexOf(row);
    } else {
      return this.contextDataSource.data.indexOf(row);
    }
  }

  private resetFilters() {
    this.searchValue.set(null);
    this.contextDataSource.filter = null;
    this.unSelectionGroups();
  }

  private resetTbFilters() {
    this.searchTbValue.set(null);
    this.contextTbDataSource.filter = null;
    this.unSelectionGroupsTb();
  }

  private resetYearsList(type: 'GL' | 'TB'): void {
    (type === 'GL') && this.gLYearsList.update((years) => {
      years.forEach(y => {
        y.isChecked = false;
        y.isDisabled = true;
      });
      return structuredClone(years);
    });
    (type === 'TB') && this.tbYearsList.update(years => {
      years.forEach(y => {
        y.isChecked = false;
        y.isDisabled = true;
      });
      return structuredClone(years);
    });
  }

  public refreshTb() {
    if (this.router.url?.includes('/dashboard/ntrdashboard/')) {
      const engInfo = JSON.parse(localStorage.getItem('selectedEngagementObj'));
      if (engInfo?.engagementsid === this.selectedEng()?.engId) {
        this.closeContext.emit({actionType: 'REFRESH', updatedContext: null});
      }
    }
  }

  public closeContextWindow() {
    this.closeContext.emit({
      actionType: 'CLOSE',
      updatedContext: JSON.parse(JSON.stringify(this.originalContext())),
      contextRes: JSON.parse(JSON.stringify(this.contextInfo()))
    });
  }

  public changeTab(tabIndex: number) {
    this.selectedIndex.set(tabIndex);
    this.cdr.detectChanges();
  }

  public onChangeTbYearsList(value: boolean, index: number) {
    this.tbYearsList.update((years) => {
      years[index].isChecked = value;
      return years;
    });
    this.updateTBTokens();
  }

  public sortTBData(sort: Sort) {
    if (!sort.active || sort.direction === '') {
      return;
    }
    if (sort.active === 'units') {
      const isAsc = sort.direction === 'asc';
      let sortingData = isAsc ? this.contextTbDataSource.data.sort((a, b) => a.totalUnits - b.totalUnits) : this.contextTbDataSource.data.sort((a, b) => b.totalUnits - a.totalUnits);
      this.bindTBGrid(sortingData);
    } else {
      let sortingData = this.contextTbDataSource.data;
      const data = (sortingData).slice();
      sortingData = data.sort((a, b) => {
        const isAsc = sort.direction === 'asc';
        switch (sort.active) {
          case 'accountCode':
            return isAsc
              ? a.accountCode.localeCompare(b.accountCode, undefined, {numeric: true})
              : b.accountCode.localeCompare(a.accountCode, undefined, {numeric: true});
          case 'name':
            return this.compare(a.name, b.name, isAsc);
          default:
            return 0;
        }
      });
      this.bindTBGrid(sortingData);
    }

  }

  private bindTBGrid(list: ContextGlTbModel[]) {
    this.contextTbDataSource = new MatTableDataSource(list);
    this.contextTbDataSource.filterPredicate = ((data: ContextGlTbModel, filter: string) => {
      const inputSearch = (this.searchTbValue() ? (data.accountCode.toLowerCase().includes(this.searchTbValue()) ||
        data.name.toLowerCase().includes(this.searchTbValue()) || data.groupName.toLowerCase().includes(this.searchTbValue()) ||
        (data.units + 'k').toLowerCase().includes(this.searchTbValue())) : true);
      const finGroup = this.groupsTb().find(group => group.isChecked) ? this.groupsTb().filter(group => (group.isChecked && group.id === data.finGroupId)).length > 0 :
        this.groupsTb().filter(group => (!group.isChecked)).length === this.groupsTb().length;
      return inputSearch && finGroup;
    }) as (ContextGlTbModel, string) => boolean;
    this.refreshTbDataSource();
  }

  private updateTBTokens() {
    const filteredKeys = this.tbYearsList()?.filter(key => key.isChecked === true);
    let len = this.tbYearsList()?.filter(key => key.isChecked)?.length;
    if (this.searchTbValue()) {
      this.contextTbDataSource.filteredData.forEach(item => {
        item.units = 0;
        item.totalUnits = 0;
      });
      const dataFilter = this.aiFreeContextUtil.updateTbDataSource(this.contextTbDataSource.filteredData, filteredKeys, len);
      this.contextTbDataSource.filteredData = dataFilter.dataSource;
    } else {
      this.contextTbDataSource.data.forEach(item => {
        item.units = 0;
        item.totalUnits = 0;
      });
      const dataFilter = this.aiFreeContextUtil.updateTbDataSource(this.contextTbDataSource.data, filteredKeys, len);
      this.contextTbDataSource.data = dataFilter.dataSource;
    }
    this.calculateTotalTokens();
  }

  private refreshTbDataSource() {
    this.isTbSelectedAll.set(
      this.searchTbValue() ?
        ((this.contextTbDataSource.filteredData?.filter(item => item.isRowChecked)?.length) === (this.contextTbDataSource.filteredData?.length)) :
        (this.contextTbDataSource.data?.filter(item => item.isRowChecked)?.length === this.contextTbDataSource.data?.length)
    );
    this.updateTBTokens();
  }

  public unSelectionGroupsTb() {
    this.groupsTb.update((groups) => {
      groups?.forEach(g => {
        g.isChecked = false;
      });
      return [...groups];
    });
    this.contextTbDataSource.filter = 'groupName';
    this.updateTBTokens();
  }

  public onChangeTbRow(value: boolean, item: ContextGlTbModel) {
    if (this.searchTbValue()) {
      this.contextTbDataSource.filteredData[this.getIndexRowTb(item)].isRowChecked = value;
    } else {
      this.contextTbDataSource.data[this.getIndexRowTb(item)].isRowChecked = value;
    }
    this.refreshTbDataSource();
  }

  private getIndexRowTb(row: ContextGlTbModel): number {
    if (this.searchTbValue()) {
      return this.contextTbDataSource.filteredData.indexOf(row);
    } else {
      return this.contextTbDataSource.data.indexOf(row);
    }
  }

  private calculateTotalTokens() {
    const glFilterKeys = this.gLYearsList()?.filter(key => key.isChecked === true);
    const tbFilterKeys = this.tbYearsList()?.filter(key => key.isChecked === true);
    const glDataSource = this.aiFreeContextUtil.updateDataSource(this.contextDataSource.data, glFilterKeys, glFilterKeys?.length);
    const tbDataSource = this.aiFreeContextUtil.updateTbDataSource(this.contextTbDataSource.data, tbFilterKeys, tbFilterKeys?.length);
    const otherApiTokens = this.calculateOATokens();
    this.tokensUsage.update(tokens => {
      tokens.totalTokens = tbDataSource.usageToken.totalTokens + glDataSource.usageToken.totalTokens + otherApiTokens;
      tokens.estimatedTokens = parseFloat((tokens.totalTokens / 1000).toFixed(1));
      tokens.selectedGlRows = this.contextDataSource.data.filter(r => {
        const groupId = this.groupsGl()?.filter(r => r.isChecked === true)?.map(r => r?.id);
        return groupId?.length > 0 ? (groupId?.includes(r?.finGroupId) && r?.isRowChecked === true) : (r?.isRowChecked === true);
      })?.length ?? 0;
      tokens.selectedTbRows = this.contextTbDataSource.data.filter(r => {
        const groupId = this.groupsTb()?.filter(g => g.isChecked === true)?.map(r => r?.id);
        return groupId?.length > 0 ? (groupId?.includes(r?.finGroupId) && r?.isRowChecked === true) : (r?.isRowChecked === true);
      })?.length ?? 0;
      tokens.selectedOARows = this.contextOADataSource.data.filter(r => r.isRowChecked === true)?.length ?? 0;
      tokens.pendingTokens = parseFloat(((this.contextInfo()?.maxTokensAllowed - tokens.totalTokens) / 1000).toFixed(1));
      return {...tokens};
    });
    if (document.getElementById('progressCircle')) {
      this.setPercentage();
    } else {
      setTimeout(() => {
        this.setPercentage();
      }, 200);
    }
  }

  private setOtherApiContext() {
    if (this.chatHistory()?.freeContext?.apiContext?.selectedApiIds?.length > 0) {
      const apiList = [...this.otherApi()];
      const historyList = [...this.chatHistory()?.freeContext?.apiContext?.selectedApiIds];
      apiList.forEach(api => {
        const selectedApi = historyList?.find(i => i.apiId === api?.apiId);
        if(selectedApi?.apiId) {
          api.isRowChecked = true;
          api.content = selectedApi?.content;
          api.tokens = selectedApi?.tokens;
          api.totalUnits = selectedApi?.tokens >= 1000 ? this.aiFreeContextUtil.numberToNotationK(selectedApi?.tokens) : selectedApi?.tokens;
          api.hasFetchedFromSource = true;
        } else {
          api.isRowChecked = false;
          api.hasFetchedFromSource = false;
        }
      });
      this.otherApi.set([...apiList]);
      this.oaYearsList.update((years) => {
        years?.forEach((y, i) => {
          if (i === 0) {
            y.isChecked = this.chatHistory()?.freeContext?.apiContext?.isCYSelected;
          }
          if (i === 1) {
            y.isChecked = this.chatHistory()?.freeContext?.apiContext?.isPY1Selected;
          }
          if (i === 2) {
            y.isChecked = this.chatHistory()?.freeContext?.apiContext?.isPY2Selected;
          }
          y.isDisabled = false;
        });
        return [...years];
      });
    } else {
      this.oaYearsList.update(years => {
        years?.forEach((y, i) => {
          y.isChecked = (i === 0);
        });
        return [...years];
      });
    }
    if(this.otherApi()?.length > 0) {
      this.bindOAGrid([...this.otherApi()]);
      this.calculateTotalTokens();
    }
  }

  public sortOAData(sort: Sort) {
    if (sort.direction === '' || !sort.active) {
      return;
    }
    if (sort?.active === 'units') {
      const isAsc = sort.direction === 'asc';
      let sortingData = isAsc ? this.contextOADataSource.data.sort((a, b) => a.totalUnits
          - b.totalUnits)
        : this.contextOADataSource.data.sort((a, b) => b.totalUnits - a.totalUnits);
      this.bindOAGrid(sortingData);
    } else {
      let sortingData = this.contextOADataSource?.data;
      const data = (sortingData).slice();
      sortingData = data.sort((a, b) => {
        const isAsc = sort.direction === 'asc';
        switch (sort.active) {
          case 'description':
            return this.compare(a.description, b.description, isAsc);
          case 'name':
            return this.compare(a.name, b.name, isAsc);
          default:
            return 0;
        }
      });
      this.bindOAGrid(sortingData);
    }

  }

  private bindOAGrid(list: ContextOtherApiModel[] = []) {
    this.contextOADataSource = new MatTableDataSource(list);
    this.contextOADataSource.filterPredicate = ((data: ContextOtherApiModel, filter: string) => {
      return (this.searchOA() ? (data?.name?.toLowerCase()?.includes(this.searchOA()) ||
        data?.description?.toLowerCase()?.includes(this.searchOA())  ||
        (data?.tokens + 'k').toLowerCase()?.includes(this.searchOA())) : true);
    }) as (ContextOtherApiModel, string) => boolean;
    this.refreshOADataSource();
  }
  private refreshOADataSource() {
    this.isOASelectedAll.set(
      this.searchOA() ?
        ((this.contextOADataSource.filteredData?.filter(g => g?.isRowChecked === true)?.length) === (this.contextOADataSource.filteredData?.length)) :
        ((this.contextOADataSource.data?.filter(g => g?.isRowChecked === true)?.length) === (this.contextOADataSource.data.length))
    );
  }

  public applyOAFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchOA.set((filterValue.trim().length ? filterValue.trim().toLowerCase() : null));
    this.contextOADataSource.filter = filterValue.trim().toLowerCase();
  }

  public selectAllOARows(isChecked: boolean) {
    this.otherApi.update(api => {
      api.forEach(a => {
          a.isRowChecked = isChecked;
          if(!isChecked) {
            a.hasFetchedFromSource = false;
          }
          if(!a.hasFetchedFromSource && isChecked) {
            a.isLoading = true;
          }
      })
      return [...api];
    });
    this.bindOAGrid([...this.otherApi()]);
    this.fetchSelectedAllOtherApi(this.aiFreeContextUtil.filterOtherApiFetchList(
      this.searchOA() ? this.contextOADataSource.filteredData : this.contextOADataSource.data, this.selectedEng()))
    this.refreshOADataSource();
    this.calculateTotalTokens();
  }

  public onChangeOAYearList(value: boolean, index: number) {
    this.oaYearsList.update(years => {
      years?.forEach((y, i) => {
        if (i === index) {
          y.isChecked = value;
        }
      });
      return [...years];
    });
    this.otherApi.update(api => {
      api?.forEach(a => {
        const {content, tokens} = this.aiFreeContextUtil.getYearSelection(this.oaYearsList(), a);
        a.content = content;
        a.tokens = tokens;
        a.totalUnits = tokens >= 1000 ? this.aiFreeContextUtil.numberToNotationK(tokens) : tokens;
      });
      return [...api];
    });
    this.bindOAGrid([...this.otherApi()]);
    this.calculateTotalTokens();
  }

  public onChangeOARow(value: boolean, item: ContextOtherApiModel) {
    if (this.searchTbValue()) {
      this.contextOADataSource.filteredData[this.getIndexRowOA(item)].isRowChecked = value;
      this.contextOADataSource.filteredData[this.getIndexRowOA(item)].isLoading = value;
    } else {
      this.contextOADataSource.data[this.getIndexRowOA(item)].isRowChecked = value;
      this.contextOADataSource.data[this.getIndexRowOA(item)].isLoading = value;
    }
    this.otherApi.update(api => {
      api.forEach(a => {
        if (a?.apiId === item?.apiId) {
          a.isRowChecked = value;
        }
      });
      return [...api];
    });
    this.refreshOADataSource();
    if(value && item?.hasFetchedFromSource) {
      this.updateOAInformationToChat(true,item?.apiId);
    }
    if(value && !item?.hasFetchedFromSource) {
      this.getSelectedOtherApiInfo(item?.apiId);
    }
    if(!value) {
      this.updateOAInformationToChat(false,item?.apiId);
    }
  }

  private getIndexRowOA(row: ContextOtherApiModel): number {
    if (this.searchTbValue()) {
      return this.contextOADataSource.filteredData.indexOf(row);
    } else {
      return this.contextOADataSource.data.indexOf(row);
    }
  }

  private calculateOATokens() {
    const selectedApis = this.contextOADataSource.data?.filter(d => d?.isRowChecked);
    const totalTokens = selectedApis?.reduce((total, item) => total + (item?.tokens ?? 0) , 0);
    return totalTokens ?? 0
  }

  private getSelectedOtherApiInfo(selectedApiId: number = 0) {
    const input : SaveOtherApiModel = {
      chat_id:  null,
      engagement_id: this.selectedEng()?.engId,
      file_path: null,
      integration_id: this.selectedEng()?.integrationId,
      year_end_date: this.selectedEng()?.engYearEnd,
      selections: [{action: 'select', apiId: selectedApiId}]
    }
    this.aiService.updateOtherApiForContext(input).pipe().subscribe({
      next: response => {
        if (response?.status === 200 && response?.data?.apiContext?.length > 0) {
          const selectedAPI = response?.data?.apiContext[0];
          this.otherApi.update(api => {
            api?.forEach(a => {
              if(a?.apiId === selectedApiId) {
                a.isRowChecked = true;
                const {content, tokens} = this.aiFreeContextUtil.getYearSelection(this.oaYearsList(), selectedAPI);
                a.content = content;
                a.tokens = tokens;
                a.totalUnits = tokens >= 1000 ? this.aiFreeContextUtil.numberToNotationK(tokens) : tokens;
                a.hasFetchedFromSource = true;
                a.isLoading = false;
                a.cy = selectedAPI?.cy;
                a.py1 = selectedAPI?.py1;
                a.py2 = selectedAPI?.py2;
              }
            })
            return [...api]
          });
        } else {
          this.otherApi.update(api => {
            api?.forEach(a => {
              if (a?.apiId === selectedApiId) {
                a.hasFetchedFromSource = true;
                a.isLoading = false;
              }
            });
            return [...api];
          });
        }
        this.bindOAGrid([...this.otherApi()]);
        this.calculateTotalTokens();
      }
    })
  }

  private updateOAInformationToChat(isChecked: boolean, apiId: number) {
    this.otherApi.update(api => {
      api.forEach(a => {
        if(a.apiId === apiId) {
          a.isRowChecked = isChecked;
          if(!isChecked) {
            a.hasFetchedFromSource = false;
          }
        }
      })
      return [...api];
    });
    this.cdr.detectChanges();
    this.bindOAGrid([...this.otherApi()]);
    this.calculateTotalTokens();
  }

  private fetchSelectedAllOtherApi(inputs: SaveOtherApiModel) {
    if(!(inputs?.selections?.length > 0)) return;
    this.aiService.updateOtherApiForContext(inputs).pipe().subscribe({
      next: response => {
        if (response?.status === 200 && response?.data?.apiContext?.length > 0) {
          const otherApi = structuredClone(this.otherApi());
          otherApi?.forEach(a => {
            const selectedAPI = response?.data?.apiContext?.find(s => s?.apiId === a?.apiId);
            if (selectedAPI?.apiId) {
              a.isRowChecked = true;
              a.isLoading = false;
              const {content, tokens} = this.aiFreeContextUtil.getYearSelection(this.oaYearsList(), selectedAPI);
              a.content = content;
              a.hasFetchedFromSource = true;
              a.tokens = tokens;
              a.totalUnits = selectedAPI?.tokens >= 1000 ? this.aiFreeContextUtil.numberToNotationK(tokens) : tokens;
            }
          });
          this.otherApi.set([...otherApi]);
          this.bindOAGrid([...this.otherApi()]);
          this.calculateTotalTokens();
        }
      }
    })
  }

}
